import dayjs from 'dayjs';
import { FieldType } from '@/components/table/_fieldDefinitions/FieldType.ts';
import { Currency } from '@/features/dashboard/paymentConfig/types/Currency.ts';
import { CustomFieldValueDto } from '@/types/CustomFieldValueDto.ts';
import {
  BillLine,
  hasBillField,
  BillSection,
  BookingBill,
} from '@/features/dashboard/bookingChanges/types/BookingBill.ts';
import { ClientBillField } from '@/features/dashboard/bookingChanges/types/dto/ClientBillField.ts';
import { isAddressLine } from '@/features/dashboard/bookingChanges/types/dto/ClientBillField.utils.ts';
import { ParticipantBillField } from '@/features/dashboard/bookingChanges/types/dto/ParticipantBillField.ts';
import {
  ChangeItemDto,
  ChangeItemType,
  isChangeItemOfType,
} from '@/features/dashboard/bookingChanges/types/dto/ChangeItemDto.ts';
import {
  InsuranceChangeOp,
  OptionChangeOp,
  TransportChangeOp,
} from '@/features/dashboard/bookingChanges/types/dto/ChangeOps.ts';
import { PriceSource } from '@/features/dashboard/bookingChanges/types/dto/PriceSource.ts';
import {
  findManualLineSection,
  manualBillLine,
  manualLineOf,
} from '@/features/dashboard/bookingChanges/utils/draftItems.ts';
import { money } from '@/features/dashboard/bookingChanges/utils/money.ts';
import { EditFieldFormData, parseAmount } from './editFieldSchema';
import { customFieldTypeOf, TOGGLE_YES } from './fieldTypes';

/**
 * Złożenie pozycji paczki z wiersza rachunku i wartości z formularza.
 *
 * Model widoku niesie sam tekst wiersza, a paczka — UUID-y i stan docelowy.
 * Tłumaczenie w drugą stronę (ekran → paczka) siedzi tutaj, żeby modal edycji
 * pozostał samą formą, a reguły dało się przetestować bez renderowania.
 *
 * PATCH podmienia całą zawartość paczki, więc wynikiem jest **komplet** pozycji
 * po zmianie (`mergeDraftItems`), a nie sama różnica.
 */

/**
 * Wiersz rachunku wraz z sekcją i osobą, do której należy (`null` —
 * zapisujący albo pozycja całej rezerwacji).
 */
export interface BillLineLocation {
  line: BillLine;
  section: BillSection;
  participantId: string | null;
}

/** Wiersz rachunku po identyfikatorze — ten sam identyfikator nosi `BillRowVm.id`. */
export const findBillLine = (
  bill: BookingBill,
  lineId: string,
): BillLineLocation | null => {
  for (const participant of bill.participants) {
    for (const section of participant.sections) {
      const line = section.lines.find((candidate) => candidate.id === lineId);
      if (line) {
        return { line, section, participantId: participant.participantId };
      }
    }
  }

  for (const section of [
    ...bill.client.sections,
    ...bill.bookingWideSections,
  ]) {
    const line = section.lines.find((candidate) => candidate.id === lineId);
    if (line) {
      return { line, section, participantId: null };
    }
  }

  return null;
};

/**
 * Sekcje osoby, do której należy pozycja ręczna. `null` oznacza rezerwację jako
 * całość (reguła D10) — sekcje zapisującego kwot nie niosą, więc wolnej linii
 * przyjąć nie mogą i szukamy wtedy wśród sekcji całej rezerwacji.
 */
const manualLineSections = (
  bill: BookingBill,
  participantId: string | null,
): BillSection[] =>
  participantId === null
    ? bill.bookingWideSections
    : (bill.participants.find(
        (participant) => participant.participantId === participantId,
      )?.sections ?? []);

/**
 * Miejsce dla pozycji ręcznej, której w szkicu jeszcze nie ma.
 *
 * `sectionId` wskazuje sekcję, z której przyszedł przycisk („+ Wolna linia”);
 * jego brak („+ Zniżka”, stojąca poza sekcjami) oddaje wybór rachunkowi —
 * bierzemy jedyną sekcję przyjmującą wolne linie. Identyfikator wiersza jest
 * z góry ustalony, bo staje się identyfikatorem samej pozycji: tylko po nim
 * `matchesLine` potrafi związać wiersz z pozycją paczki.
 */
export const newManualLineLocation = (
  bill: BookingBill,
  participantId: string | null,
  sectionId: string | null,
  lineId: string,
  label: string,
): BillLineLocation | null => {
  const sections = manualLineSections(bill, participantId);
  const section = sectionId
    ? sections.find(
        (candidate) => candidate.id === sectionId && candidate.withAmounts,
      )
    : findManualLineSection(sections);

  if (!section) {
    return null;
  }

  return {
    line: manualBillLine(lineId, label, participantId),
    section,
    participantId,
  };
};

/**
 * Wiersz rachunku po identyfikatorze — razem z wierszami, których rachunek nie
 * zna, bo dopisał je sam szkic. Bez tego pozycja ręczna byłaby widoczna na
 * ekranie, ale nie dałoby się jej ani poprawić, ani usunąć.
 */
export const findChangeLine = (
  bill: BookingBill,
  items: ChangeItemDto[],
  lineId: string,
): BillLineLocation | null => {
  const location = findBillLine(bill, lineId);
  if (location) {
    return location;
  }

  const item = items
    .filter(isChangeItemOfType(ChangeItemType.MANUAL_LINE))
    .find((candidate) => candidate.id === lineId);
  if (!item) {
    return null;
  }

  const sections = manualLineSections(bill, item.participantId);
  const section = findManualLineSection(sections);

  return section
    ? { line: manualLineOf(item), section, participantId: item.participantId }
    : null;
};

/**
 * Czy kontrakt paczki potrafi w ogóle wyrazić zmianę tego wiersza.
 *
 * Brak odsyłacza znaczy wprost „paczka nie ma dla tego wiersza typu pozycji” —
 * zniżki i korekty rachunku oraz pola tylko do odczytu.
 *
 * Wiersz wielowartościowy (`chips`) wchodzi do edycji tylko wtedy, gdy rachunek
 * wymienia wybrane warianty z identyfikatorami (`ref.currentVariantIds`) —
 * pozycja paczki niesie jeden wariant, więc bez tej listy nie da się złożyć
 * pozycji `REMOVE` dla wariantu, którego wiersz nie nazywa po UUID-zie.
 */
export const isEditableLine = ({ line }: BillLineLocation): boolean => {
  const { ref } = line;
  if (!ref) {
    return false;
  }
  if (ref.kind === ChangeItemType.OPTION) {
    return line.chips === null || ref.currentVariantIds !== null;
  }
  return true;
};

/**
 * Pozycja szkicu zajmująca ten sam „slot”, co edytowany wiersz.
 *
 * Slot jest szerszy niż wiersz: dane uczestnika, dane zapisującego i wartości
 * pól niestandardowych mieszczą się w **jednej** pozycji paczki na osobę.
 * Bez tego edycja drugiego pola tej samej osoby założyłaby drugą pozycję tego
 * samego typu, a `toPatchRequest` wysłałby dwie zmiany dla jednego uczestnika.
 */
export const findSlotItem = (
  items: ChangeItemDto[],
  { line, participantId }: BillLineLocation,
): ChangeItemDto | undefined =>
  items.find((item) => {
    const { ref } = line;
    if (!ref) {
      return false;
    }

    switch (ref.kind) {
      case ChangeItemType.BASE_PRICE:
        return (
          item.type === ChangeItemType.BASE_PRICE &&
          item.participantId === participantId
        );
      case ChangeItemType.OPTION:
        return (
          item.type === ChangeItemType.OPTION &&
          item.participantId === participantId &&
          item.tourOptionId === ref.targetId
        );
      case ChangeItemType.TRANSPORT:
        return (
          item.type === ChangeItemType.TRANSPORT &&
          item.participantId === participantId
        );
      case ChangeItemType.INSURANCE:
        return (
          item.type === ChangeItemType.INSURANCE &&
          item.participantId === participantId
        );
      case ChangeItemType.CUSTOM_VALUES:
        return (
          item.type === ChangeItemType.CUSTOM_VALUES &&
          item.participantId === participantId
        );
      case ChangeItemType.PARTICIPANT_DATA:
        return (
          item.type === ChangeItemType.PARTICIPANT_DATA &&
          item.participantId === participantId
        );
      case ChangeItemType.CLIENT_DATA:
        return item.type === ChangeItemType.CLIENT_DATA;
      case ChangeItemType.MANUAL_LINE:
        // Slot wolnej linii to ona sama — wiersz nosi identyfikator pozycji,
        // bo nic innego w rachunku jej nie odpowiada.
        return (
          item.type === ChangeItemType.MANUAL_LINE && item.id === ref.targetId
        );
      default:
        return false;
    }
  });

/**
 * Wszystkie pozycje szkicu zajmujące slot edytowanego wiersza.
 *
 * Wiersz wielowartościowy trzyma ich tyle, ile wariantów operator dołożył albo
 * zdjął — `findSlotItem` (pierwsza pasująca) pokazałaby wtedy tylko jedną
 * z nich, a PATCH ma nieść komplet.
 */
export const findSlotItems = (
  items: ChangeItemDto[],
  location: BillLineLocation,
): ChangeItemDto[] => {
  if (!isMultiValueOptionLine(location.line)) {
    const slotItem = findSlotItem(items, location);
    return slotItem ? [slotItem] : [];
  }

  return items.filter(
    (item) =>
      item.type === ChangeItemType.OPTION &&
      item.participantId === location.participantId &&
      item.tourOptionId === location.line.ref?.targetId,
  );
};

/** Wiersz opcji z wyborem wielokrotnym — rachunek wymienia wybrane warianty. */
export const isMultiValueOptionLine = (line: BillLine): boolean =>
  line.ref?.kind === ChangeItemType.OPTION &&
  line.ref.currentVariantIds !== null;

/**
 * Wiersz „Imię i nazwisko” — jedna linijka ekranu, dwa pola kontraktu.
 * Dotyczy tak samo uczestnika, jak i zapisującego, bo oba enumy pól niosą
 * `FIRST_NAME` i `LAST_NAME` o tych samych wartościach.
 */
export const isPersonNameLine = (line: BillLine): boolean =>
  hasBillField(line.ref, ParticipantBillField.FIRST_NAME) ||
  hasBillField(line.ref, ParticipantBillField.LAST_NAME);

export interface BuildChangeItemParams {
  location: BillLineLocation;
  type: FieldType;
  values: EditFieldFormData;
  /** Pozycja zajmująca ten sam slot — nowa wartość dosiada się do niej. */
  slotItem?: ChangeItemDto;
  /** Identyfikator nadawany pozycji, gdy slot jest pusty. */
  newItemId: string;
}

const toIsoDate = (date: Date | null): string | null =>
  date ? dayjs(date).format('YYYY-MM-DD') : null;

/** Wartość pola tekstowego formularza; pusty napis znaczy „nie podano”. */
const textOrNull = (value: string): string | null => value.trim() || null;

/**
 * Wartość pola niestandardowego. Typ przechodzi jeszcze raz przez
 * `customFieldTypeOf`, choć `useEditField` zawęża go już przy otwieraniu
 * modala — pozycję paczki składa się też poza tą ścieżką, a kontrakt
 * (`TableFieldType.supportsCustomValues()`) obowiązuje niezależnie od tego,
 * kto ją buduje.
 */
const toCustomFieldValue = (
  type: FieldType,
  text: string,
): CustomFieldValueDto => {
  switch (customFieldTypeOf(type)) {
    case FieldType.CHECKBOX:
      return { type: FieldType.CHECKBOX, value: text === TOGGLE_YES };
    case FieldType.LONG_TEXT:
      return { type: FieldType.LONG_TEXT, value: text };
    case FieldType.DROPDOWN:
      return { type: FieldType.DROPDOWN, value: text };
    case FieldType.TEXT:
      return { type: FieldType.TEXT, value: text };
  }
};

/**
 * Pozycja paczki opisująca zmianę wiersza — albo `null`, gdy wiersz nic nie
 * zmienia (pusty wybór na pustym wierszu) lub kontrakt go nie obejmuje.
 */
export const buildChangeItem = ({
  location,
  type,
  values,
  slotItem,
  newItemId,
}: BuildChangeItemParams): ChangeItemDto | null => {
  const { line, participantId } = location;
  const { ref } = line;
  if (!ref) {
    return null;
  }

  const text = values.value.trim();
  // Formularz ma jedno pole kwoty, więc pozycja powstaje w jednej walucie.
  // Wiersz rachunku narzuca swoją; pozycja ręczna żadnej nie ma, więc walutę
  // wskazuje operator w formularzu.
  const currency = line.amount[0]?.currency ?? values.currency;
  const id = slotItem?.id ?? newItemId;

  const priceFields = {
    priceSource: values.priceOverridden
      ? PriceSource.MANUAL
      : PriceSource.CATALOG,
    priceOverride: values.priceOverridden
      ? money(parseAmount(values.price) ?? 0, currency)
      : null,
    priceNote: values.priceOverridden ? values.priceNote.trim() || null : null,
  };

  switch (ref.kind) {
    case ChangeItemType.BASE_PRICE: {
      const amount = parseAmount(values.value);
      if (amount === null) {
        return null;
      }
      return {
        type: ChangeItemType.BASE_PRICE,
        id,
        participantId,
        priceOverride: money(amount, currency),
        priceNote: values.priceNote.trim() || null,
      };
    }

    case ChangeItemType.MANUAL_LINE: {
      const amount = parseAmount(values.value);
      const description = values.description.trim();
      // Encja odrzuca pusty opis, a pozycja na zero nic do rachunku nie wnosi —
      // jedno i drugie zatrzymuje walidacja formularza, ale kontrakt paczki
      // trzyma się tego również wtedy, gdy pozycję składa kto inny.
      if (amount === null || amount === 0 || description === '') {
        return null;
      }

      return {
        type: ChangeItemType.MANUAL_LINE,
        // Identyfikatorem pozycji jest identyfikator jej wiersza — inaczej po
        // zapisie wiersz przestałby się z nią wiązać i szkic dopisałby drugi.
        id: ref.targetId ?? id,
        participantId,
        description,
        priceOverride: money(amount, currency),
        priceNote: values.priceNote.trim() || null,
      };
    }

    case ChangeItemType.OPTION: {
      const tourOptionId = ref.targetId;
      const previousVariantId = ref.currentVariantId;
      const newVariantId = text || null;

      if (!tourOptionId || (!previousVariantId && !newVariantId)) {
        return null;
      }

      return {
        type: ChangeItemType.OPTION,
        id,
        participantId,
        op: !previousVariantId
          ? OptionChangeOp.ADD
          : !newVariantId
            ? OptionChangeOp.REMOVE
            : OptionChangeOp.CHANGE,
        tourOptionId,
        removedVariantId: previousVariantId,
        newVariantId,
        ...priceFields,
      };
    }

    case ChangeItemType.TRANSPORT: {
      const previousVariantId = ref.currentVariantId;
      const newVariantId = text || null;

      if (!previousVariantId && !newVariantId) {
        return null;
      }

      return {
        type: ChangeItemType.TRANSPORT,
        id,
        participantId,
        op: !previousVariantId
          ? TransportChangeOp.SET
          : !newVariantId
            ? TransportChangeOp.CLEAR
            : TransportChangeOp.CHANGE,
        previousVariantId,
        newVariantId,
        ...priceFields,
      };
    }

    case ChangeItemType.INSURANCE: {
      const previousInsuranceId = ref.currentVariantId;
      const newInsuranceId = text || null;

      if (!previousInsuranceId && !newInsuranceId) {
        return null;
      }

      return {
        type: ChangeItemType.INSURANCE,
        id,
        participantId,
        op: !previousInsuranceId
          ? InsuranceChangeOp.SET
          : !newInsuranceId
            ? InsuranceChangeOp.CLEAR
            : InsuranceChangeOp.CHANGE,
        previousInsuranceId,
        newInsuranceId,
      };
    }

    case ChangeItemType.CUSTOM_VALUES: {
      const fieldId = ref.targetId;
      if (!fieldId) {
        return null;
      }

      const previousValues =
        slotItem?.type === ChangeItemType.CUSTOM_VALUES
          ? (slotItem.newCustomValues ?? {})
          : {};

      return {
        type: ChangeItemType.CUSTOM_VALUES,
        id,
        participantId,
        newCustomValues: {
          ...previousValues,
          [fieldId]: toCustomFieldValue(
            type,
            type === FieldType.DATE ? (toIsoDate(values.date) ?? '') : text,
          ),
        },
      };
    }

    case ChangeItemType.PARTICIPANT_DATA: {
      const previous =
        slotItem?.type === ChangeItemType.PARTICIPANT_DATA ? slotItem : null;
      // Wiersz „Imię i nazwisko” niesie oba pola naraz, więc formularz pyta
      // o każde osobno — nic tu nie trzeba rozklejać po spacji.
      const changesName = isPersonNameLine(line);

      return {
        type: ChangeItemType.PARTICIPANT_DATA,
        id,
        participantId,
        newFirstName: changesName
          ? textOrNull(values.firstName)
          : (previous?.newFirstName ?? null),
        newLastName: changesName
          ? textOrNull(values.lastName)
          : (previous?.newLastName ?? null),
        newBirthDate: hasBillField(ref, ParticipantBillField.BIRTH_DATE)
          ? toIsoDate(values.date)
          : (previous?.newBirthDate ?? null),
      };
    }

    case ChangeItemType.CLIENT_DATA: {
      const previous =
        slotItem?.type === ChangeItemType.CLIENT_DATA ? slotItem : null;
      const changesName = isPersonNameLine(line);

      return {
        type: ChangeItemType.CLIENT_DATA,
        id,
        participantId: null,
        newFirstName: changesName
          ? textOrNull(values.firstName)
          : (previous?.newFirstName ?? null),
        newLastName: changesName
          ? textOrNull(values.lastName)
          : (previous?.newLastName ?? null),
        newEmail: hasBillField(ref, ClientBillField.EMAIL)
          ? text || null
          : (previous?.newEmail ?? null),
        newPhoneNumber: hasBillField(ref, ClientBillField.PHONE)
          ? values.phoneNumber
          : (previous?.newPhoneNumber ?? null),
        newBirthDate: hasBillField(ref, ClientBillField.BIRTH_DATE)
          ? toIsoDate(values.date)
          : (previous?.newBirthDate ?? null),
        // Wiersz wymienia trzy pola adresu osobno, a `ClientDataChangeRequest`
        // niesie je jednym obiektem — tutaj składamy je z powrotem w `AddressDto`.
        newAddress: isAddressLine(ref)
          ? {
              street: values.street.trim(),
              postalCode: values.postalCode.trim(),
              city: values.city.trim(),
            }
          : (previous?.newAddress ?? null),
      };
    }

    default:
      return null;
  }
};

/**
 * Rozkład zmiany wiersza wielowartościowego na pozycje paczki.
 *
 * `OptionChangeItemDto` niesie **jeden** wariant, więc wybór wielokrotny
 * zapisuje się jako kilka pozycji: `ADD` dla każdego wariantu dołożonego
 * i `REMOVE` dla każdego zdjętego (backend wprost tego oczekuje — pole
 * `removedVariantId` jest opisane jako „przy multi-selekcie mówi, KTÓRY wariant
 * zdjąć”). Pozycja już będąca w szkicu zachowuje swój identyfikator, żeby PATCH
 * nie zakładał jej od nowa przy każdym zapisie.
 *
 * Ceny nie nadpisujemy: kwota wiersza jest sumą kilku wariantów, więc jedno
 * pole „kwota” nie miałoby do czego się przypiąć.
 */
const buildOptionSelectionItems = (
  { line, participantId }: BillLineLocation,
  selected: string[],
  slotItems: ChangeItemDto[],
  newItemId: () => string,
): ChangeItemDto[] => {
  const tourOptionId = line.ref?.targetId;
  if (!tourOptionId) {
    return [];
  }

  const current = line.ref?.currentVariantIds ?? [];
  const optionItems = slotItems.filter(
    isChangeItemOfType(ChangeItemType.OPTION),
  );

  const itemId = (op: OptionChangeOp, variantId: string): string =>
    optionItems.find(
      (item) =>
        item.op === op &&
        (op === OptionChangeOp.ADD
          ? item.newVariantId
          : item.removedVariantId) === variantId,
    )?.id ?? newItemId();

  const optionItem = (
    op: OptionChangeOp,
    variantId: string,
  ): ChangeItemDto => ({
    type: ChangeItemType.OPTION,
    id: itemId(op, variantId),
    participantId,
    op,
    tourOptionId,
    removedVariantId: op === OptionChangeOp.REMOVE ? variantId : null,
    newVariantId: op === OptionChangeOp.ADD ? variantId : null,
    priceSource: PriceSource.CATALOG,
    priceOverride: null,
    priceNote: null,
  });

  return [
    ...selected
      .filter((variantId) => !current.includes(variantId))
      .map((variantId) => optionItem(OptionChangeOp.ADD, variantId)),
    ...current
      .filter((variantId) => !selected.includes(variantId))
      .map((variantId) => optionItem(OptionChangeOp.REMOVE, variantId)),
  ];
};

export interface BuildChangeItemsParams {
  location: BillLineLocation;
  type: FieldType;
  values: EditFieldFormData;
  /** Pozycje zajmujące ten sam slot — nowe wartości dosiadają się do nich. */
  slotItems?: ChangeItemDto[];
  /** Identyfikator dla pozycji, której w szkicu jeszcze nie ma. */
  newItemId: () => string;
}

/**
 * Pozycje paczki opisujące zmianę wiersza. Pusta lista znaczy „nic do zapisania”
 * — wiersz albo nic nie zmienia, albo kontrakt go nie obejmuje.
 */
export const buildChangeItems = ({
  location,
  type,
  values,
  slotItems = [],
  newItemId,
}: BuildChangeItemsParams): ChangeItemDto[] => {
  if (isMultiValueOptionLine(location.line)) {
    return buildOptionSelectionItems(
      location,
      values.values,
      slotItems,
      newItemId,
    );
  }

  const item = buildChangeItem({
    location,
    type,
    values,
    slotItem: slotItems[0],
    newItemId: newItemId(),
  });

  return item ? [item] : [];
};

/**
 * Komplet pozycji szkicu po zmianie — pozycje zajmujące slot zostają
 * podmienione w miejscu, nadmiarowe dopisane na końcu, brakujące usunięte.
 * Wynik idzie wprost do `usePatchDraft`.
 */
export const mergeDraftItems = (
  items: ChangeItemDto[],
  next: ChangeItemDto[],
  slotItems: ChangeItemDto[] = [],
): ChangeItemDto[] => {
  const replacedIds = new Set(slotItems.map((item) => item.id));
  const pending = [...next];

  const merged = items.flatMap((item) => {
    if (!replacedIds.has(item.id)) {
      return [item];
    }
    const substitute = pending.shift();
    return substitute ? [substitute] : [];
  });

  return [...merged, ...pending];
};
